import { Link } from "@mui/material";

import {
  faqs,
  howItWorks,
  WichitaWindSurgeLink,
  purchaseSeatsLanguages,
} from "./helpers";
import { routes } from "@/global/routes";
import {
  DefaultCheckoutTerms,
  DefaultRegistrationTerms,
} from "../../default_terms_and_conditions";
import { vendorUUIDs } from "../../constants";

import StaticSeatMap from "public/images/wichitawindsurge_flex/SeasonTicketFlyers.png";
import FindGamesBannerImage from "public/images/wichitawindsurge_flex/AttendSpecials-4-1486x148-2ndAttendPack.png";

import type { TeamConfig } from "@/global/hooks/useTeamConfig";

const config: TeamConfig = {
  teamSchema: "wichitawindsurge_flex",
  browserTitle: "Wichita Wind Surge Flex Pack",
  packages: false,
  defaultRoute: routes.purchaseSeats,
  phoneNumber: "",
  teamName: "Wichita Wind Surge",

  // Seat map configs
  seatMapConfigs: {
    venueId: "",
    mapId: "",
    staticSeatMapImage: StaticSeatMap.src,
    isShowAvailableSeatsButtonHidden: true,
    isView3dButtonHidden: true,
    customSeatMapStaticImageStyle: {
      width: "50%",
      display: "block",
      marginX: "auto",
    },
  },

  // Gift cards
  giftCardConfigs: {
    isMenuGiftCardEnabled: false,
    isLandingPageGiftCardEnabled: false,
    isStandalonePageGiftCardEnabled: false,
    redirectGiftCardUrl: "",
    redeemSuccessModalLink: "",
    hideRedeemItem: false,
    customShareGiftUrl: "",
    customErrorMessage: "",
    hideRecipient: false,
  },

  dynamicPricing: {
    enabled: true,
    showAllDiscounts: true,
    list: [
      {
        id: 1,
        minSelectedAmount: 2,
        updatedAccountGroup: 0,
        discount: 15,
        customProgressBarMessage:
          "Congrats! You've unlocked 15% off your tickets! Add 2 more games for 20% off, add 4 more games for 25% off, add 6 games for Fireworks on the Field experience or 8+ games for an exclusive batting practice experience!",
      },
      {
        id: 2,
        minSelectedAmount: 3,
        updatedAccountGroup: 0,
        discount: 15,
        hideOfferFromModal: true,
        customProgressBarMessage:
          "Add 1 more game for 20% off, add 3 more games for 25% off, add 5 games for Fireworks on the Field or 7+ games for an exclusive batting practice experience!",
      },
      {
        id: 3,
        minSelectedAmount: 4,
        updatedAccountGroup: 1,
        discount: 20,
        customProgressBarMessage:
          "Congrats! You've unlocked 20% off your tickets! Add 2 more games for 25% off, 4 games for exclusive Fireworks on the Field or 6+ games for an exclusive batting practice experience!",
      },
      {
        id: 4,
        minSelectedAmount: 5,
        updatedAccountGroup: 1,
        discount: 20,
        hideOfferFromModal: true,
        customProgressBarMessage:
          "Add 1 more game for 25% off, add 3 games for Fireworks on the Field, or 5+ games for an exclusive batting practice experience!",
      },
      {
        id: 5,
        minSelectedAmount: 6,
        updatedAccountGroup: 2,
        discount: 25,
        customProgressBarMessage:
          "Congrats! You have unlocked 25% off your seats! Add 2 more games for Fireworks on the Field or 4+ games for an exclusive batting practice experience.",
      },
      {
        id: 6,
        minSelectedAmount: 7,
        updatedAccountGroup: 2,
        discount: 25,
        hideOfferFromModal: true,
        customProgressBarMessage:
          "Add 1 more game for Fireworks on the Field or 3+ games for an exclusive batting practice experience!",
      },
      {
        id: 7,
        minSelectedAmount: 8,
        updatedAccountGroup: 3,
        discount: 25,
        customAditionalMessage: " for Fireworks on the Field experience",
        customProgressBarMessage:
          "Congrats! You've unlocked Fireworks on the Field! Add 2+ games for an exclusive batting practice experience or checkout now.",
      },
      {
        id: 8,
        minSelectedAmount: 9,
        updatedAccountGroup: 2,
        discount: 25,
        hideOfferFromModal: true,
        customProgressBarMessage:
          "Add 1 more game for an exclusive batting practice experience!",
      },
      {
        id: 9,
        minSelectedAmount: 10,
        updatedAccountGroup: 4,
        discount: 25,
        customAditionalMessage:
          " for an exclusive batting practice experience?",
        customProgressBarMessage:
          "Congrats! You've unlocked an exclusive batting practice experience! Add more games or checkout now.",
      },
    ],
  },

  // Purchase credit configs
  purchaseCreditConfigs: {
    isLandingPurchaseCreditEnabled: false,
    isStandAlonePurchaseCreditEnabled: false,
    showPurchaseCreditLinkInHomeMenu: false,
    isPurchaseCreditHistoryEnabled: false,
  },

  // Disabled routes
  disabledRoutes: [routes.giftCard],

  // Switches
  checkoutCreditPackages: false,
  availableCreditEnabled: false,
  isCrowdHandlerEnabled: true,
  inventoryType: "SEAT-LEVEL",

  // Purchase seat configs
  purchaseSeatConfigs: {
    minGameSelectionCount: 2,
    maxGameSelectionCount: 12,
    minSeatLimit: 1,
    isSimilarSeatForAllEvents: true,
    allowManualCartInSimilar: true,
    isCheckoutEditSeatsEnabled: true,
    isDiffSeatAlertEnabled: true,
    hideViewTicketsInThankYouPage: false,
    defaultSeatCount: 2,
    checkoutTimerEnabled: false,
    isDisabledEventSelection: false,
    hideAllUpcomingGames: false,
    isRepProduct: false,
  },

  // Feature configs
  packageConfigs: {
    isPackageEnabled: false,
    canRemovePackageGames: false,
    staticBuildYourOwn: null,
  },

  // Intercom settings
  intercomConfigs: {
    isIntercomEnabled: true,
    allowedPages: [],
  },

  // Checkout validations
  checkoutValidation: {
    allowedZipFailCount: Infinity,
    allowedPaymentFailCount: Infinity,
  },

  isSaveCardAllowed: true,
  isBrainTreePaymentEnabled: false,
  shouldUseInternalLogin: false,
  oktaClientId: "0oasmcdgh92Jom7TI357",
  oktaIssuer: "https://ids.mlb.com/oauth2/aus1m088yK07noBfh356",
  platform: vendorUUIDs.tickesdotcom, // TDC platform
  refreshInventoryWhenEventClick: false,

  // Analytics configs
  analyticsConfigs: {
    googleAnalyticsId: "",
    attrkPixelId: "",
    beaconId: "",
    metaPixelId: "",
  },

  // Redirect configs
  redirectConfigs: {
    internalRedirectUrl: "",
    externalRedirectUrl: "",
  },

  // URLs
  accountManagerUrl:
    "https://mlb.tickets.com/?agency=MILB_MPV&orgid=56869#/auth/login",

  // Images
  images: {
    findGamesBannerImage: FindGamesBannerImage.src,
    authBackgroundImage: FindGamesBannerImage.src, // Using findGamesBannerImage
    checkoutStatusPageImage: FindGamesBannerImage.src,
    logoImage:
      "https://ss-leagues.s3.amazonaws.com/Minor League Baseball/Wichita_Wind_Surge_logo.png?width=128&height=75",
    logoNameImage: "",
    faviconImage:
      "https://ss-leagues.s3.amazonaws.com/Minor League Baseball/Wichita_Wind_Surge_logo.png",
    seatMap3dPreviewImage: "",
  },

  uiConfigs: {
    bannerImage: {
      title: "",
      isBackgroundBasedBanner: false,
    },
    gameFiltersSettings: {
      hideAccessibilityFilter: false,
      hideSectionFilter: false,
    },
    featuredGameCard: {
      showLogoWithTitle: false,
      hideOverlayLogo: true,
    },
    bottomBar: {
      showGameSelectionProgress: true,
    },
    progressBar: {
      staticProgressBarText: false,
      customProgressBarSize: 600,
    },
    customAdaLabel: (
      <>
        To purchase accessible seating locations, please contact the Box Office
        at{" "}
        <Link
          href="tel:3162218000"
          sx={{
            fontWeight: 500,
            color: "primary.main",
            textDecoration: "none",
          }}
        >
          (316) 221-8000
        </Link>
        .
      </>
    ),
    checkoutStatusPage: {
      checkoutSuccessMessageFontWeight: "bold",
    },
  },

  colors: {
    primary: "#032c5b",
  },

  howItWorks,
  faqs,

  languages: {
    topNavMenuItems: "",
    checkoutTermsAndCondition: <DefaultCheckoutTerms />,
    registrationTermsAndCondition: <DefaultRegistrationTerms />,
    purchaseSeats: purchaseSeatsLanguages,
    giftCard: {
      title: "",
      createGiftCard: {
        description: "",
        recipient: {
          sendNowFalse: {
            description: "",
          },
          sendNowTrue: {
            description: "",
          },
        },
      },
      redeemGiftCard: {
        loggedIn: {
          description: "",
        },
        logout: {
          description: "",
        },
        success: {
          description: "",
        },
      },
    },
    purchaseGiftCard: null,
    redeemGiftCard: null,
    checkoutSuccessMessage: "Your purchase is complete!",
    checkoutSuccessDescription:
      "Your Wichita Wind Surge tickets are confirmed! Your tickets are available to be managed.",
    signInAuthText:
      "Connect your mlb.com account to continue with seamless access.",
  },
};

export default config;
